import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import JsBarcode from "jsbarcode";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { 
  Settings, 
  Building2, 
  Phone, 
  CreditCard,
  Receipt,
  Save,
  RotateCcw,
  Loader2,
  FolderTree,
  Plus,
  Trash2,
  Link,
  Mail,
  MailX,
  Image,
  Upload,
  X,
  Send,
  Clock,
  Calendar,
  Zap,
  Gift,
  Printer,
} from "lucide-react";
import { useVat } from "@/context/VatContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Module-level cache so the sample calibration barcode is only built once
// per page load (the data URL is ~6 KB; rebuilding on every render is wasted
// work, and using useMemo here would violate hook ordering because the call
// site sits after an `if (loading) return …` early return).
const _sampleBarcodeCache = { value: null };

export const CompanySettings = () => {
  const { refreshVatSetting } = useVat();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: "",
    short_name: "",
    logo_url: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "",
    postal_code: "",
    phone: "",
    fax: "",
    email: "",
    website: "",
    vat_number: "",
    registration_number: "",
    vat_enabled: true, // Whether store charges VAT
    receipt_footer_line1: "",
    receipt_footer_line2: "",
    receipt_footer_line3: "",
    receipt_footer_line4: "",
    receipt_footer_line5: "",
    bank_name: "",
    bank_account_number: "",
    bank_branch_code: "",
    bank_account_type: ""
  });
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    emails_enabled: false,
    auto_send_invoice_on_account_sale: true,
    auto_send_invoice_on_customer_sale: true
  });
  const [savingEmail, setSavingEmail] = useState(false);
  
  // VAT Company profile state
  const [vatCompany, setVatCompany] = useState({
    company_name: "", vat_number: "", registration_number: "",
    address_line1: "", address_line2: "", city: "", province: "", postal_code: "",
    phone: "", email: "",
    bank_name: "", bank_account_number: "", bank_branch_code: "", bank_account_type: "",
    loan_interest_rate: 20,
  });
  const [savingVatCompany, setSavingVatCompany] = useState(false);

  // Category/Department management state
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [selectedCategoryForDept, setSelectedCategoryForDept] = useState("");
  const [loadingCatDept, setLoadingCatDept] = useState(false);
  
  // Data utilities state
  const [syncingRecons, setSyncingRecons] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncingLocations, setSyncingLocations] = useState(false);
  const [locationSyncResult, setLocationSyncResult] = useState(null);
  const [profitBackfillRunning, setProfitBackfillRunning] = useState(false);
  const [profitBackfillResult, setProfitBackfillResult] = useState(null);
  const [profitAuditRunning, setProfitAuditRunning] = useState(false);
  const [profitAuditResult, setProfitAuditResult] = useState(null);

  // Invoice payment re-evaluation state
  const [reevaluating, setReevaluating] = useState(false);
  const [reevaluateResult, setReevaluateResult] = useState(null);
  const [applyingReeval, setApplyingReeval] = useState(false);
  const [reevalCustomerFilter, setReevalCustomerFilter] = useState("");

  // System Valuation report state
  const [valuationSending, setValuationSending] = useState(false);
  const [valuationEmail, setValuationEmail] = useState("herman@bosveldbh.co.za");

  // Sticker print calibration state
  const [stickerOffsetX, setStickerOffsetX] = useState(-2);
  const [stickerOffsetY, setStickerOffsetY] = useState(0);
  const [savingCalibration, setSavingCalibration] = useState(false);

  // Daily Summary Email state
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [dailySummaryPreview, setDailySummaryPreview] = useState(null);
  const [dailySummarySending, setDailySummarySending] = useState(false);
  const [dailySummaryDate, setDailySummaryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Flash VAS state
  const [flashSettings, setFlashSettings] = useState({ api_key_set: false, account_number: '', environment: 'sandbox', enabled: false });
  const [flashApiKey, setFlashApiKey] = useState('');
  const [flashAccountNumber, setFlashAccountNumber] = useState('');
  const [flashEnvironment, setFlashEnvironment] = useState('sandbox');
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [flashSaving, setFlashSaving] = useState(false);

  // Loyalty cashback state
  const [loyaltySettings, setLoyaltySettings] = useState({
    enabled: true,
    auto_post_enabled: true,
    cashback_percent: 1.0,
    low_spend_percent: 1.5,
    high_spend_percent: 0.5,
    exclude_markup_threshold: 5.0,
    no_cap: true,
    monthly_cap_amount: null
  });
  const [savingLoyalty, setSavingLoyalty] = useState(false);
  const [previewingLoyalty, setPreviewingLoyalty] = useState(false);

  const fetchLoyaltySettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/loyalty-cashback`);
      setLoyaltySettings({
        enabled: res.data.enabled ?? true,
        auto_post_enabled: res.data.auto_post_enabled ?? true,
        cashback_percent: res.data.cashback_percent ?? 1.0,
        low_spend_percent: res.data.low_spend_percent ?? 1.5,
        high_spend_percent: res.data.high_spend_percent ?? 0.5,
        exclude_markup_threshold: res.data.exclude_markup_threshold ?? 5.0,
        no_cap: res.data.no_cap ?? true,
        monthly_cap_amount: res.data.monthly_cap_amount ?? null,
      });
    } catch (e) {
      console.error("Failed to fetch loyalty settings:", e);
    }
  };

  const [runningLoyalty, setRunningLoyalty] = useState(false);
  const [loyaltyPreview, setLoyaltyPreview] = useState(null);
  const [loyaltyDupeBusy, setLoyaltyDupeBusy] = useState(false);
  const [loyaltyMonth, setLoyaltyMonth] = useState(new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const [loyaltyYear, setLoyaltyYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());


  // Flash VAS settings fetch - defined before useEffect to avoid hoisting issues
  const fetchFlashSettings = async () => {
    try {
      const res = await axios.get(`${API}/flash/settings`);
      setFlashSettings(res.data);
      setFlashAccountNumber(res.data.account_number || '');
      setFlashEnvironment(res.data.environment || 'sandbox');
      setFlashEnabled(res.data.enabled || false);
    } catch (e) {
      console.error("Failed to fetch Flash settings:", e);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCategoriesDepartments();
    fetchFlashSettings();
    fetchEmailSettings();
    fetchLoyaltySettings();
    // Fetch VAT company profile
    axios.get(`${API}/settings/vat-company`).then(res => setVatCompany(res.data)).catch(() => {});
  }, []);
  
  // Logo upload functions
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file too large. Maximum size is 2MB.");
      return;
    }
    
    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        // Upload to server
        const formData = new FormData();
        formData.append('image_data', base64Data);
        
        const response = await axios.post(`${API}/settings/upload-logo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
          toast.success("Logo uploaded successfully");
        }
        setUploadingLogo(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo");
      setUploadingLogo(false);
    }
  };
  
  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/settings/remove-logo`);
      setSettings(prev => ({ ...prev, logo_url: "" }));
      toast.success("Logo removed");
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove logo");
    }
  };
  
  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      setEmailSettings(response.data);
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  };
  
  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await saveWithRetry(() => axios.put(`${API}/settings/email`, emailSettings));
      toast.success("Email settings saved");
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast.error(errorMessage(error, "Failed to save email settings"));
    } finally {
      setSavingEmail(false);
    }
  };
  
  const fetchCategoriesDepartments = async () => {
    try {
      const response = await axios.get(`${API}/categories-departments`);
      setCategories(response.data.categories || []);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Error fetching categories/departments:", error);
    }
  };
  
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    setLoadingCatDept(true);
    try {
      await axios.post(`${API}/categories-departments`, {
        name: newCategoryName.trim(),
        type: "category"
      });
      toast.success(`Category "${newCategoryName}" added`);
      setNewCategoryName("");
      fetchCategoriesDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add category");
    } finally {
      setLoadingCatDept(false);
    }
  };
  
  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      toast.error("Please enter a department name");
      return;
    }
    if (!selectedCategoryForDept) {
      toast.error("Please select a category for this department");
      return;
    }
    setLoadingCatDept(true);
    try {
      await axios.post(`${API}/categories-departments`, {
        name: newDepartmentName.trim(),
        type: "department",
        category_id: selectedCategoryForDept
      });
      const catName = categories.find(c => c.id === selectedCategoryForDept)?.name;
      toast.success(`Department "${newDepartmentName}" added to ${catName}`);
      setNewDepartmentName("");
      fetchCategoriesDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add department");
    } finally {
      setLoadingCatDept(false);
    }
  };

  const handleDeleteCategoryOrDepartment = async (id, name, type) => {
    const typeLabel = type === 'category' ? 'category' : 'department';
    if (!window.confirm(`Are you sure you want to delete the ${typeLabel} "${name}"?\n\nNote: Products using this ${typeLabel} will need to be reassigned.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/categories-departments/${id}`);
      toast.success(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} "${name}" deleted`);
      fetchCategoriesDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${typeLabel}`);
    }
  };
  
  const handleAssignDepartmentToCategory = async (departmentId, categoryId) => {
    try {
      await axios.put(`${API}/departments/${departmentId}/category?category_id=${categoryId}`);
      const catName = categories.find(c => c.id === categoryId)?.name;
      toast.success(`Department assigned to ${catName}`);
      fetchCategoriesDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign department");
    }
  };
  
  // Group departments by category
  const getDepartmentsByCategory = (categoryId) => {
    return departments.filter(d => d.category_id === categoryId);
  };
  
  const unassignedDepartments = departments.filter(d => !d.category_id);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setSettings(response.data);
      if (typeof response.data.sticker_offset_x_mm === 'number') setStickerOffsetX(response.data.sticker_offset_x_mm);
      if (typeof response.data.sticker_offset_y_mm === 'number') setStickerOffsetY(response.data.sticker_offset_y_mm);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load company settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/company`, settings);
      toast.success("Company settings saved successfully");
      // Refresh the VAT setting in the app context
      refreshVatSetting();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all settings to defaults?")) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/settings/company/reset`);
      setSettings(response.data);
      toast.success("Settings reset to defaults");
      // Refresh the VAT setting in the app context
      refreshVatSetting();
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast.error("Failed to reset settings");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Sync reconciliation data utility function
  const handleSyncReconciliations = async () => {
    setSyncingRecons(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${API}/customers/sync-reconciliations`);
      setSyncResult(response.data);
      toast.success(`Synced ${response.data.invoices_updated} invoices`);
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error(error.response?.data?.detail || "Failed to sync reconciliation data");
    } finally {
      setSyncingRecons(false);
    }
  };

  const handleSyncLocations = async () => {
    setSyncingLocations(true);
    setLocationSyncResult(null);
    try {
      const response = await axios.post(`${API}/products/sync-locations`);
      setLocationSyncResult(response.data);
      toast.success(`Healed ${response.data.healed} of ${response.data.scanned} products`);
    } catch (error) {
      console.error("Error syncing locations:", error);
      toast.error(error.response?.data?.detail || "Failed to sync product locations");
    } finally {
      setSyncingLocations(false);
    }
  };

  const handleProfitAudit = async () => {
    setProfitAuditRunning(true);
    setProfitAuditResult(null);
    try {
      const response = await axios.get(`${API}/sales/admin/profit-audit`);
      setProfitAuditResult(response.data);
      toast.success(`Audited ${response.data.sales_counted} sales`);
    } catch (error) {
      console.error("Audit failed:", error);
      toast.error(error.response?.data?.detail || "Audit failed");
    } finally {
      setProfitAuditRunning(false);
    }
  };

  // Cost-price backfill (fixes inflated historical "Gross Profit")
  const handleProfitBackfillDryRun = async () => {
    setProfitBackfillRunning(true);
    setProfitBackfillResult(null);
    try {
      const response = await axios.post(`${API}/sales/admin/backfill-cost-prices?dry_run=true`);
      setProfitBackfillResult(response.data);
      toast.success(
        `${response.data.items_fixed} line item(s) would be fixed across ${response.data.sales_updated} sale(s)`
      );
    } catch (error) {
      console.error("Backfill dry-run failed:", error);
      toast.error(error.response?.data?.detail || "Dry-run failed");
    } finally {
      setProfitBackfillRunning(false);
    }
  };

  const handleProfitBackfillRun = async () => {
    if (!window.confirm(
      "Run cost-price backfill?\n\nThis updates every sale where a line item has missing or 0 cost_price, stamping the matching product's current cost. Existing real costs are never overwritten. Recommended to run \"Preview\" first."
    )) return;
    setProfitBackfillRunning(true);
    setProfitBackfillResult(null);
    try {
      const response = await axios.post(`${API}/sales/admin/backfill-cost-prices`);
      setProfitBackfillResult(response.data);
      toast.success(
        `Fixed ${response.data.items_fixed} line item(s) across ${response.data.sales_updated} sale(s)`
      );
    } catch (error) {
      console.error("Backfill failed:", error);
      toast.error(error.response?.data?.detail || "Backfill failed");
    } finally {
      setProfitBackfillRunning(false);
    }
  };

  // Re-evaluate invoice payments (DRY-RUN by default)
  const handleReevaluateInvoices = async (apply = false) => {
    if (apply) {
      const total = reevaluateResult?.summary?.mismatches_found || 0;
      if (!window.confirm(
        `This will UPDATE ${total} invoice(s) — resetting amount_paid to the value backed by reconciliation records. Continue?`
      )) return;
      setApplyingReeval(true);
    } else {
      setReevaluating(true);
      setReevaluateResult(null);
    }
    try {
      const params = new URLSearchParams();
      params.set("dry_run", apply ? "false" : "true");
      if (reevalCustomerFilter && reevalCustomerFilter.trim()) {
        params.set("customer_id", reevalCustomerFilter.trim());
      }
      const response = await axios.post(
        `${API}/customers/admin/reevaluate-invoice-payments?${params.toString()}`
      );
      setReevaluateResult(response.data);
      if (apply) {
        toast.success(
          `${response.data.summary.invoices_corrected} invoice(s) corrected. Re-run dry-run to confirm.`
        );
      } else {
        const found = response.data.summary.mismatches_found;
        toast.success(
          found > 0
            ? `Found ${found} mismatched invoice(s) — review below before applying`
            : "No mismatches found — invoice payments look correct"
        );
      }
    } catch (error) {
      console.error("Error re-evaluating invoices:", error);
      toast.error(error.response?.data?.detail || "Failed to re-evaluate invoice payments");
    } finally {
      setReevaluating(false);
      setApplyingReeval(false);
    }
  };

  // Generate & email the System Valuation PDF
  const handleEmailSystemValuation = async () => {
    if (!valuationEmail || !valuationEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setValuationSending(true);
    try {
      const response = await axios.post(
        `${API}/system-valuation/email`,
        { to_email: valuationEmail }
      );
      toast.success(
        `Valuation PDF emailed to ${response.data.to_email} (${response.data.filename})`
      );
    } catch (error) {
      console.error("Error emailing system valuation:", error);
      toast.error(error.response?.data?.detail || "Failed to email valuation report");
    } finally {
      setValuationSending(false);
    }
  };

  // Download the System Valuation PDF directly
  const handleDownloadSystemValuation = () => {
    window.open(`${API}/system-valuation/pdf`, "_blank");
  };

  // Save sticker calibration offsets
  const handleSaveCalibration = async () => {
    setSavingCalibration(true);
    try {
      await axios.put(`${API}/settings/company`, {
        sticker_offset_x_mm: stickerOffsetX,
        sticker_offset_y_mm: stickerOffsetY,
      });
      setSettings((prev) => ({ ...prev, sticker_offset_x_mm: stickerOffsetX, sticker_offset_y_mm: stickerOffsetY }));
      toast.success(`Calibration saved (X: ${stickerOffsetX}mm, Y: ${stickerOffsetY}mm)`);
    } catch (error) {
      console.error("Error saving calibration:", error);
      toast.error("Failed to save calibration");
    } finally {
      setSavingCalibration(false);
    }
  };

  // Generate a CODE128 barcode as data URL — matches the renderer used by
  // ShelfTalkerModal so the calibration test sticker looks identical to the
  // real shelf-talker output the cashier prints from Stock Master.
  const calBarcodeDataUrl = (code) => {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, code, {
        format: "CODE128",
        width: 3,
        height: 80,
        displayValue: true,
        fontSize: 22,
        margin: 5,
        textMargin: 3,
        font: "Arial",
        fontOptions: "bold",
        background: "#ffffff",
        lineColor: "#000000",
      });
      return canvas.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  // Sample barcode for the calibration preview / test print. Lazily memoized
  // via a module-level cache so we don't rebuild the canvas every render and
  // so we stay clear of hook-order rules (this is called after the
  // `if (loading) return …` early-return in the component).
  const SAMPLE_BARCODE = "6009675432189";
  if (!_sampleBarcodeCache.value) {
    _sampleBarcodeCache.value = calBarcodeDataUrl(SAMPLE_BARCODE);
  }
  const sampleBarcodeImg = _sampleBarcodeCache.value;

  // Print a single test sticker using the CURRENT (unsaved) offsets so user can dial in
  const handleTestPrintSticker = () => {
    const PRINT_HOST_ID = 'bh-cal-print-host';
    const PRINT_STYLE_ID = 'bh-cal-print-style';
    document.getElementById(PRINT_HOST_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();

    const sampleBarcode = SAMPLE_BARCODE;
    const barcodeImg = sampleBarcodeImg || calBarcodeDataUrl(sampleBarcode);

    const host = document.createElement('div');
    host.id = PRINT_HOST_ID;
    host.innerHTML = `
      <div class="bh-label">
        <div class="bh-row1">
          <div class="bh-logo">${(settings.short_name || 'BBH').slice(0, 6)}</div>
          <div class="bh-price">R16.90</div>
        </div>
        <div class="bh-row2">
          <span class="bh-plu">PLU: 3554</span>
          <span class="bh-loc">Shelf:P1-9 Bin:0002</span>
        </div>
        ${barcodeImg ? `<img class="bh-barcode" src="${barcodeImg}" alt="barcode" />` : ''}
        <div class="bh-name">PVC GUTTER HALF ROUND BRACKET</div>
      </div>`;
    document.body.appendChild(host);

    const ox = stickerOffsetX || 0;
    const oy = stickerOffsetY || 0;
    // Wider safe padding (2.5mm × 1.5mm) leaves ±2mm horizontal slack so
    // calibration nudges never push the "P" in PLU or the bin number off
    // the physical sticker. Bumped from 1mm × 0.5mm.
    const padTop = 1.5;
    const padRight = 2.5;
    const padBottom = 1.5;
    const padLeft = 2.5;
    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      #${PRINT_HOST_ID} { display: none; }
      @media print {
        @page { size: 40mm 30mm; margin: 0; }
        body > *:not(#${PRINT_HOST_ID}) { display: none !important; }
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
        #${PRINT_HOST_ID} { display: block !important; margin: 0 !important; padding: 0 !important; }
        #${PRINT_HOST_ID} .bh-label {
          width: 40mm; height: 30mm;
          padding: ${padTop}mm ${padRight}mm ${padBottom}mm ${padLeft}mm;
          transform: translate(${ox}mm, ${oy}mm); transform-origin: top left;
          display: flex; flex-direction: column;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
          color: #000;
          overflow: hidden;
        }
        #${PRINT_HOST_ID} .bh-row1 { display: flex; justify-content: space-between; align-items: center; }
        #${PRINT_HOST_ID} .bh-logo { font-size: 9pt; font-weight: 900; background: #000; color: #fff; padding: 0.3mm 1.5mm; letter-spacing: 0.3mm; }
        #${PRINT_HOST_ID} .bh-price { font-size: 13pt; font-weight: 900; color: #000; white-space: nowrap; }
        #${PRINT_HOST_ID} .bh-row2 { display: flex; justify-content: space-between; align-items: center; font-size: 7pt; font-weight: bold; margin-top: 0.2mm; }
        #${PRINT_HOST_ID} .bh-barcode { width: 36mm; height: auto; max-height: 11mm; object-fit: contain; margin: 0 auto; display: block; }
        #${PRINT_HOST_ID} .bh-name { font-size: 8pt; text-align: center; font-weight: bold; line-height: 1.0; text-transform: uppercase; margin-top: auto; overflow: hidden; max-height: 6mm; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `;
    document.head.appendChild(style);

    const cleanup = () => {
      try { host.remove(); } catch (_) {}
      try { style.remove(); } catch (_) {}
      try {
        const badge = document.getElementById('emergent-badge');
        if (badge && badge.dataset.bhPrintOldDisplay !== undefined) {
          badge.style.display = badge.dataset.bhPrintOldDisplay;
          delete badge.dataset.bhPrintOldDisplay;
        }
      } catch (_) {}
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    try {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.dataset.bhPrintOldDisplay = badge.style.display || '';
        badge.style.display = 'none';
      }
    } catch (_) {}

    setTimeout(() => {
      try {
        window.print();
      } catch (e) {
        console.error('Test print error:', e);
        toast.error('Print failed. Try the regular Print button or check your printer.');
      }
      setTimeout(cleanup, 4000);
    }, 100);
  };

  const saveFlashSettings = async () => {
    setFlashSaving(true);
    try {
      await axios.put(`${API}/flash/settings`, {
        api_key: flashApiKey || undefined,
        account_number: flashAccountNumber,
        environment: flashEnvironment,
        enabled: flashEnabled,
      });
      toast.success("Flash VAS settings saved");
      setFlashApiKey('');
      fetchFlashSettings();
    } catch (e) {
      toast.error("Failed to save Flash settings");
    } finally {
      setFlashSaving(false);
    }
  };

  // Daily Summary Email functions
  const handlePreviewDailySummary = async () => {
    setDailySummaryLoading(true);
    setDailySummaryPreview(null);
    try {
      const response = await axios.get(`${API}/daily-summary/preview?date=${dailySummaryDate}`);
      setDailySummaryPreview(response.data);
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Failed to fetch daily summary preview");
    } finally {
      setDailySummaryLoading(false);
    }
  };

  const handleSendDailySummary = async () => {
    if (!window.confirm("Send the daily sales summary email to bosveldbh@gmail.com?")) {
      return;
    }
    setDailySummarySending(true);
    try {
      const response = await axios.post(`${API}/daily-summary/send?date=${dailySummaryDate}`);
      if (response.data.status === "success") {
        toast.success("Daily summary email sent successfully!");
        setDailySummaryPreview(response.data);
      } else {
        toast.error(response.data.message || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send daily summary email");
    } finally {
      setDailySummarySending(false);
    }
  };

  const handleSaveLoyaltySettings = async () => {
    setSavingLoyalty(true);
    try {
      await axios.put(`${API}/settings/loyalty-cashback`, loyaltySettings);
      toast.success("Loyalty cashback settings saved");
      fetchLoyaltySettings();
    } catch (error) {
      console.error("Failed to save loyalty settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save loyalty settings");
    } finally {
      setSavingLoyalty(false);
    }
  };

  const handlePreviewLoyalty = async () => {
    setPreviewingLoyalty(true);
    setLoyaltyPreview(null);
    try {
      const response = await axios.post(`${API}/settings/loyalty-cashback/preview`, null, {
        params: {
          year: loyaltyYear,
          month: loyaltyMonth,
          cashback_percent: loyaltySettings.cashback_percent,
        },
      });
      setLoyaltyPreview(response.data);
      toast.success("Loyalty preview generated");
    } catch (error) {
      console.error("Failed to preview loyalty cashback:", error);
      toast.error(error.response?.data?.detail || "Failed to preview loyalty cashback");
    } finally {
      setPreviewingLoyalty(false);
    }
  };

  const handleRunLoyalty = async () => {
    if (!window.confirm(`Post loyalty cashback credits for ${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')} now?`)) {
      return;
    }

    setRunningLoyalty(true);
    try {
      const response = await axios.post(`${API}/settings/loyalty-cashback/run`, null, {
        params: {
          year: loyaltyYear,
          month: loyaltyMonth,
          initiated_by: "Admin",
          force: false,
        },
      });
      const posted = response.data.posted_count || 0;
      const monthLabel = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
      if (posted === 0) {
        toast.warning(
          `⚠️ Cashback run for ${monthLabel} completed but credited 0 customers. ` +
          `Click Preview to see why (usually: sales missing customer_id, or all items below the markup threshold).`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Loyalty cashback posted to ${posted} customers for ${monthLabel}`);
      }
      await handlePreviewLoyalty();
    } catch (error) {
      console.error("Failed to run loyalty cashback:", error);
      toast.error(error.response?.data?.detail || "Failed to run loyalty cashback");
    } finally {
      setRunningLoyalty(false);
    }
  };

  const handleFixFutureDatedCredits = async () => {
    const monthLabel = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
    if (!window.confirm(
      `Fix future-dated loyalty credits for ${monthLabel}?\n\n` +
      `Any loyalty CREDIT journals dated AFTER today (SAST) will be rewritten ` +
      `to today's date so they appear on customer statements immediately.\n\n` +
      `Safe & idempotent — journals already dated ≤ today are skipped.`
    )) {
      return;
    }
    setRunningLoyalty(true);
    try {
      const response = await axios.post(`${API}/loyalty/fix-future-dated-credits`, null, {
        params: { year: loyaltyYear, month: loyaltyMonth, dry_run: false },
      });
      const updated = response.data.updated_count || 0;
      if (updated === 0) {
        toast.info(`No future-dated loyalty credits found for ${monthLabel}. Nothing to fix.`);
      } else {
        toast.success(`Re-dated ${updated} loyalty credit(s) for ${monthLabel} to today. They should now appear on customer statements.`);
      }
    } catch (error) {
      console.error("Failed to fix future-dated credits:", error);
      toast.error(error.response?.data?.detail || "Failed to fix future-dated credits");
    } finally {
      setRunningLoyalty(false);
    }
  };

  const handleForceRepostLoyalty = async () => {
    const monthLabel = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
    if (!window.confirm(
      `Force re-post loyalty cashback for ${monthLabel}?\n\n` +
      `This runs the cashback again with force=true, credit anyone who was NOT credited before.\n` +
      `Anyone already credited for ${monthLabel} is automatically skipped (per-customer dedup) — no doubles.`
    )) {
      return;
    }
    setRunningLoyalty(true);
    try {
      const response = await axios.post(`${API}/settings/loyalty-cashback/run`, null, {
        params: {
          year: loyaltyYear,
          month: loyaltyMonth,
          initiated_by: "Admin-Force",
          force: true,
        },
      });
      const posted = response.data.posted_count || 0;
      if (posted === 0) {
        toast.warning(
          `Force re-post ran but credited 0 additional customers for ${monthLabel}. ` +
          `Everyone eligible was already credited, or none of the preview rows qualified.`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Force re-post credited ${posted} customer(s) for ${monthLabel}`);
      }
      await handlePreviewLoyalty();
    } catch (error) {
      console.error("Failed to force re-post loyalty:", error);
      toast.error(error.response?.data?.detail || "Failed to force re-post loyalty");
    } finally {
      setRunningLoyalty(false);
    }
  };

  const handleExcludeFromLoyalty = async (customerId, customerName) => {
    if (!window.confirm(`Remove ${customerName} from the loyalty list for this month?`)) return;
    try {
      const monthKey = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
      await axios.post(`${API}/loyalty/exclude`, null, {
        params: { customer_id: customerId, customer_name: customerName, month_key: monthKey },
      });
      toast.success(`${customerName} removed from loyalty list`);
      await handlePreviewLoyalty();
    } catch (error) {
      toast.error("Failed to remove customer");
    }
  };

  const handleRestoreToLoyalty = async (customerId) => {
    try {
      const monthKey = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
      await axios.delete(`${API}/loyalty/exclude/${customerId}`, {
        params: { month_key: monthKey },
      });
      toast.success("Customer restored to loyalty list");
      await handlePreviewLoyalty();
    } catch (error) {
      toast.error("Failed to restore customer");
    }
  };

  const handleRestoreOverReversed = async () => {
    const monthKey = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
    const confirmed = window.confirm(
      `Restore over-reversed loyalty credits for ${monthKey}?\n\n` +
      `This finds every customer whose ENTIRE month cashback was reversed ` +
      `(i.e. both the duplicate AND the legitimate credit) and:\n` +
      `  • un-reverses the OLDEST credit\n` +
      `  • deletes its offsetting DEBIT (‑REV)\n` +
      `  • restores the loyalty balance\n\n` +
      `Safe to run — customers who still have an active credit are skipped.`
    );
    if (!confirmed) return;
    setLoyaltyDupeBusy(true);
    try {
      const res = await axios.post(`${API}/loyalty/restore-over-reversed/${monthKey}`);
      const r = res.data;
      toast.success(
        `Restored ${r.customers_restored} customer(s) — R ${r.total_amount_restored.toFixed(2)} added back to loyalty balances.`,
        { duration: 8000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Restore failed");
    } finally {
      setLoyaltyDupeBusy(false);
    }
  };

  const handleScanLoyaltyDuplicates = async () => {
    const monthKey = `${loyaltyYear}-${String(loyaltyMonth).padStart(2, '0')}`;
    setLoyaltyDupeBusy(true);
    try {
      // 1) Scan (dry run) — tell the user what would be reversed BEFORE we touch anything.
      const scan = await axios.get(`${API}/loyalty/duplicate-scan/${monthKey}`);
      const s = scan.data;
      if (!s.total_customers) {
        toast.success(`No loyalty duplicates found for ${monthKey}. All clean. ✅`);
        return;
      }
      const confirmed = window.confirm(
        `Loyalty duplicate scan for ${monthKey}\n\n` +
        `Customers affected : ${s.total_customers}\n` +
        `Duplicate journals : ${s.affected_customers.reduce((n, c) => n + c.duplicate_journal_refs.length, 0)}\n` +
        `Total to reverse   : R ${s.total_duplicate_amount.toFixed(2)}\n\n` +
        `For each affected customer the OLDEST cashback credit is kept and every later duplicate is reversed.\n\n` +
        `Proceed?`
      );
      if (!confirmed) return;

      const res = await axios.post(`${API}/loyalty/reverse-duplicates/${monthKey}`);
      const r = res.data;
      // Surface any per-journal skip/error reasons — otherwise the user
      // can't tell WHY the reverse didn't complete when the toast shows 0.
      const problems = (r.details || []).filter((d) => d.status !== "reversed" && d.status !== "zombie_repaired");
      const repaired = (r.details || []).filter((d) => d.status === "zombie_repaired").length;
      if (problems.length) {
        // eslint-disable-next-line no-console
        console.warn("Loyalty reverse — problems:", problems);
        toast.warning(
          `Reversed ${r.journals_reversed} of ${r.details.length}${repaired ? ` (+${repaired} zombie repaired)` : ""} — ${problems.length} skipped. First reason: ${problems[0].detail || problems[0].status}`,
          { duration: 12000 }
        );
      } else {
        const zombieNote = repaired ? ` and repaired ${repaired} zombie reversal(s)` : "";
        toast.success(
          `Reversed ${r.journals_reversed} duplicate credit(s) across ${r.customers_affected} customer(s)${zombieNote} — R ${r.amount_reversed.toFixed(2)} removed from loyalty balances.`,
          { duration: 8000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Duplicate scan/reverse failed");
    } finally {
      setLoyaltyDupeBusy(false);
    }
  };


  const saveVatCompany = async () => {
    setSavingVatCompany(true);
    try {
      await axios.put(`${API}/settings/vat-company`, vatCompany);
      toast.success("VAT company profile saved");
    } catch (e) {
      toast.error("Failed to save VAT company profile");
    } finally {
      setSavingVatCompany(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">Company Settings</h1>
            <p className="text-gray-500">Configure your business information for receipts and invoices</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="company">
            <Building2 className="h-4 w-4 mr-2" />
            Company Info
          </TabsTrigger>
          <TabsTrigger value="contact">
            <Phone className="h-4 w-4 mr-2" />
            Contact Details
          </TabsTrigger>
          <TabsTrigger value="receipt">
            <Receipt className="h-4 w-4 mr-2" />
            Receipt Settings
          </TabsTrigger>
          <TabsTrigger value="banking">
            <CreditCard className="h-4 w-4 mr-2" />
            Banking Details
          </TabsTrigger>
          <TabsTrigger value="email">
            {emailSettings.emails_enabled ? (
              <Mail className="h-4 w-4 mr-2 text-green-600" />
            ) : (
              <MailX className="h-4 w-4 mr-2 text-red-500" />
            )}
            Email
          </TabsTrigger>
          {/* VAT Company tab hidden — Bosveld now has its own VAT number and
              no longer issues VITEX FARMING tax invoices. Historical VITEX
              reprints still pull the snapshot stored on each original invoice
              document, so the backend endpoints/data remain intact — only the
              UI entry point is hidden. */}
          {false && (
            <TabsTrigger value="vat-company">
              <Receipt className="h-4 w-4 mr-2" />
              VAT Company
            </TabsTrigger>
          )}
          <TabsTrigger value="categories">
            <FolderTree className="h-4 w-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="loyalty">
            <Gift className="h-4 w-4 mr-2" />
            Loyalty
          </TabsTrigger>
          <TabsTrigger value="utilities">
            <RotateCcw className="h-4 w-4 mr-2" />
            Data Utilities
          </TabsTrigger>
        </TabsList>

        {/* Company Info Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={settings.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    placeholder="Your Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="short_name">Short Name / Abbreviation</Label>
                  <Input
                    id="short_name"
                    value={settings.short_name}
                    onChange={(e) => handleChange("short_name", e.target.value)}
                    placeholder="e.g., BBH"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Company Logo
                </Label>
                <p className="text-xs text-gray-500">
                  Your logo will appear on all printed and emailed documents (invoices, orders, statements, reports)
                </p>
                
                <div className="flex items-start gap-4 mt-2">
                  {/* Logo Preview */}
                  <div className="w-32 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                    {settings.logo_url ? (
                      <img 
                        src={settings.logo_url} 
                        alt="Company Logo" 
                        className="max-w-full max-h-full object-contain p-1"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<span class="text-gray-400 text-xs">Load Error</span>';
                        }}
                      />
                    ) : (
                      <div className="text-center text-gray-400">
                        <Image className="h-8 w-8 mx-auto opacity-50" />
                        <span className="text-xs">No Logo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload/Remove Buttons */}
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploadingLogo ? "Uploading..." : settings.logo_url ? "Change Logo" : "Upload Logo"}
                    </Button>
                    
                    {settings.logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveLogo}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Logo
                      </Button>
                    )}
                    
                    <p className="text-xs text-gray-400">
                      Max 2MB • PNG, JPG, WEBP
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    value={settings.vat_number}
                    onChange={(e) => handleChange("vat_number", e.target.value)}
                    placeholder="VAT Registration Number"
                    disabled={!settings.vat_enabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Company Registration Number</Label>
                  <Input
                    id="registration_number"
                    value={settings.registration_number}
                    onChange={(e) => handleChange("registration_number", e.target.value)}
                    placeholder="Company Reg Number"
                  />
                </div>
              </div>

              {/* VAT Toggle */}
              <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="vat_toggle" className="text-base font-semibold text-amber-900">
                      My store does not charge VAT
                    </Label>
                    <p className="text-sm text-amber-700 mt-1">
                      Enable this if you are not VAT registered. All VAT calculations will be disabled throughout the system.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="vat_toggle"
                      checked={!settings.vat_enabled}
                      onChange={(e) => handleChange("vat_enabled", !e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
                {!settings.vat_enabled && (
                  <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded text-sm text-green-800">
                    ✓ VAT is currently <strong>disabled</strong>. All prices are treated as VAT-exclusive and no VAT will be added or displayed.
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line1">Address Line 1</Label>
                <Input
                  id="address_line1"
                  value={settings.address_line1}
                  onChange={(e) => handleChange("address_line1", e.target.value)}
                  placeholder="Street Address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={settings.address_line2}
                  onChange={(e) => handleChange("address_line2", e.target.value)}
                  placeholder="Building, Suite, etc."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={settings.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="province">Province</Label>
                  <Input
                    id="province"
                    value={settings.province}
                    onChange={(e) => handleChange("province", e.target.value)}
                    placeholder="Province"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={settings.postal_code}
                    onChange={(e) => handleChange("postal_code", e.target.value)}
                    placeholder="Postal Code"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Details Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={settings.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="012 345 6789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fax">Store Number</Label>
                  <Input
                    id="fax"
                    value={settings.fax}
                    onChange={(e) => handleChange("fax", e.target.value)}
                    placeholder="012 345 6780"
                    data-testid="company-store-number-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="sales@company.co.za"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={settings.website}
                    onChange={(e) => handleChange("website", e.target.value)}
                    placeholder="www.company.co.za"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receipt Settings Tab */}
        <TabsContent value="receipt">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Receipt Footer Messages
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                These messages will appear at the bottom of receipts and invoices. You can add up to 5 lines.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="receipt_footer_line1">Footer Line 1</Label>
                <Input
                  id="receipt_footer_line1"
                  value={settings.receipt_footer_line1}
                  onChange={(e) => handleChange("receipt_footer_line1", e.target.value)}
                  placeholder="e.g., BAIE DANKIE VIR U ONDERSTEUNING!"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipt_footer_line2">Footer Line 2</Label>
                <Input
                  id="receipt_footer_line2"
                  value={settings.receipt_footer_line2}
                  onChange={(e) => handleChange("receipt_footer_line2", e.target.value)}
                  placeholder="e.g., THANK YOU FOR YOUR SUPPORT!"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipt_footer_line3">Footer Line 3</Label>
                <Input
                  id="receipt_footer_line3"
                  value={settings.receipt_footer_line3}
                  onChange={(e) => handleChange("receipt_footer_line3", e.target.value)}
                  placeholder="e.g., GAAN GROOT!! / GO BIG!!"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipt_footer_line4">Footer Line 4 (Optional)</Label>
                <Input
                  id="receipt_footer_line4"
                  value={settings.receipt_footer_line4 || ""}
                  onChange={(e) => handleChange("receipt_footer_line4", e.target.value)}
                  placeholder="e.g., Additional message or quote"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="receipt_footer_line5">Footer Line 5 (Optional)</Label>
                <Input
                  id="receipt_footer_line5"
                  value={settings.receipt_footer_line5 || ""}
                  onChange={(e) => handleChange("receipt_footer_line5", e.target.value)}
                  placeholder="e.g., Website or social media"
                />
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                <p className="text-sm font-medium mb-2">Receipt Footer Preview:</p>
                <div className="text-center text-sm font-mono border-t pt-2 uppercase">
                  {settings.receipt_footer_line1 && <p className="font-bold">{settings.receipt_footer_line1}</p>}
                  {settings.receipt_footer_line2 && <p className="font-bold">{settings.receipt_footer_line2}</p>}
                  {settings.receipt_footer_line3 && <p className="font-bold">{settings.receipt_footer_line3}</p>}
                  {settings.receipt_footer_line4 && <p>{settings.receipt_footer_line4}</p>}
                  {settings.receipt_footer_line5 && <p>{settings.receipt_footer_line5}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banking Details Tab */}
        <TabsContent value="banking">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Banking Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Banking details will appear on invoices and statements for customer payments.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={settings.bank_name}
                    onChange={(e) => handleChange("bank_name", e.target.value)}
                    placeholder="e.g., FNB, Standard Bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_type">Account Type</Label>
                  <Input
                    id="bank_account_type"
                    value={settings.bank_account_type}
                    onChange={(e) => handleChange("bank_account_type", e.target.value)}
                    placeholder="e.g., Cheque, Business"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input
                    id="bank_account_number"
                    value={settings.bank_account_number}
                    onChange={(e) => handleChange("bank_account_number", e.target.value)}
                    placeholder="Account Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_branch_code">Branch Code</Label>
                  <Input
                    id="bank_branch_code"
                    value={settings.bank_branch_code}
                    onChange={(e) => handleChange("bank_branch_code", e.target.value)}
                    placeholder="Branch Code"
                  />
                </div>
              </div>

              {/* Preview */}
              {settings.bank_name && (
                <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                  <p className="text-sm font-medium mb-2">Banking Details Preview:</p>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Bank:</span> {settings.bank_name}</p>
                    {settings.bank_account_type && <p><span className="font-medium">Account Type:</span> {settings.bank_account_type}</p>}
                    {settings.bank_account_number && <p><span className="font-medium">Account No:</span> {settings.bank_account_number}</p>}
                    {settings.bank_branch_code && <p><span className="font-medium">Branch Code:</span> {settings.bank_branch_code}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Loan Settings */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Loan Settings</CardTitle>
              <CardDescription>Configure default interest rate for customer loans</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="loan_interest_rate">Default Interest Rate (%)</Label>
                <Input
                  id="loan_interest_rate"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={settings.loan_interest_rate}
                  onChange={(e) => handleChange("loan_interest_rate", parseFloat(e.target.value) || 0)}
                  placeholder="e.g. 20"
                  data-testid="loan-interest-rate-setting"
                />
                <p className="text-xs text-gray-500">
                  This rate is applied automatically when processing a loan. Currently: {settings.loan_interest_rate || 20}%
                  (e.g. R 1000 loan = R {(1000 * (1 + (settings.loan_interest_rate || 20) / 100)).toFixed(2)} total)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cash Withdrawal Settings */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Cash Withdrawal Settings</CardTitle>
              <CardDescription>Configure default service fee for cash withdrawals (CASH OUT)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="withdrawal_fee_rate">Default Service Fee (%)</Label>
                <Input
                  id="withdrawal_fee_rate"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={settings.withdrawal_fee_rate ?? 5}
                  onChange={(e) => handleChange("withdrawal_fee_rate", parseFloat(e.target.value) || 0)}
                  placeholder="e.g. 5"
                  data-testid="withdrawal-fee-rate-setting"
                />
                <p className="text-xs text-gray-500">
                  This fee is charged on cash withdrawals. Currently: {settings.withdrawal_fee_rate ?? 5}%
                  (e.g. R 1000 withdrawal = R {(1000 * (settings.withdrawal_fee_rate ?? 5) / 100).toFixed(2)} fee, customer pays R {(1000 * (1 + (settings.withdrawal_fee_rate ?? 5) / 100)).toFixed(2)} total)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cart Heavy-Load Warning */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Cart Heavy-Load Warning</CardTitle>
              <CardDescription>Threshold (kg) at which the cashier sees a heavy-load alert on the running total</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="cart_weight_warning_kg">Heavy-Load Threshold (kg)</Label>
                <Input
                  id="cart_weight_warning_kg"
                  type="number"
                  step="50"
                  min="0"
                  value={settings.cart_weight_warning_kg ?? 1000}
                  onChange={(e) => handleChange("cart_weight_warning_kg", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                  placeholder="e.g. 1000"
                  data-testid="cart-weight-warning-setting"
                />
                <p className="text-xs text-gray-500">
                  When a cart's combined weight reaches this value the cashier will see a red truck warning under the VAT total. Set to 0 to disable.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {emailSettings.emails_enabled ? (
                  <Mail className="h-5 w-5 text-green-600" />
                ) : (
                  <MailX className="h-5 w-5 text-red-500" />
                )}
                Email Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Master Email Toggle */}
              <div className={`p-4 rounded-lg border-2 ${emailSettings.emails_enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold">
                      {emailSettings.emails_enabled ? '✅ Emails are ON' : '❌ Emails are OFF'}
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      {emailSettings.emails_enabled 
                        ? 'The system WILL send emails to customers (invoices, statements, etc.)' 
                        : 'The system will NOT send any emails. All outgoing emails are blocked.'}
                    </p>
                  </div>
                  <Switch
                    checked={emailSettings.emails_enabled}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, emails_enabled: checked })}
                    className="scale-125"
                    data-testid="global-email-toggle"
                  />
                </div>
              </div>

              {/* Auto-send options removed — both ACCOUNT SALE and CUSTOMER SALE
                  invoice emails are now opt-in per sale via the checkbox on the
                  checkout dialog. Same pattern as customer-journal and
                  account-receipt emails. */}

              {/* Save Button */}
              <div className="pt-4 border-t">
                <Button onClick={handleSaveEmailSettings} disabled={savingEmail}>
                  {savingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Email Settings
                    </>
                  )}
                </Button>
              </div>

              {/* Flash VAS Settings */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Flash VAS (Airtime, Electricity, Data)
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${flashSettings.api_key_set ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {flashSettings.api_key_set ? 'API Key Set' : 'Mock Mode (No API Key)'}
                      </span>
                      <span className="text-xs text-gray-500 capitalize">{flashEnvironment}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Enable VAS</Label>
                      <Switch checked={flashEnabled} onCheckedChange={setFlashEnabled} data-testid="flash-enable-toggle" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Flash API Key</Label>
                      <Input
                        type="password"
                        value={flashApiKey}
                        onChange={(e) => setFlashApiKey(e.target.value)}
                        placeholder={flashSettings.api_key_set ? "••••••••  (leave blank to keep)" : "Enter API Key from Flash"}
                        data-testid="flash-api-key-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Account Number</Label>
                      <Input
                        value={flashAccountNumber}
                        onChange={(e) => setFlashAccountNumber(e.target.value)}
                        placeholder="27XXXXXXXXX"
                        data-testid="flash-account-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Environment</Label>
                    <div className="flex gap-2 mt-1">
                      <Button size="sm" variant={flashEnvironment === 'sandbox' ? 'default' : 'outline'} onClick={() => setFlashEnvironment('sandbox')}>Sandbox</Button>
                      <Button size="sm" variant={flashEnvironment === 'production' ? 'default' : 'outline'} onClick={() => setFlashEnvironment('production')} className={flashEnvironment === 'production' ? 'bg-green-600' : ''}>Production</Button>
                    </div>
                  </div>
                  <Button onClick={saveFlashSettings} disabled={flashSaving} data-testid="flash-save-btn">
                    {flashSaving ? "Saving..." : "Save Flash Settings"}
                  </Button>
                  <p className="text-xs text-gray-400">
                    Contact <span className="font-medium">integrations@flash.co.za</span> to get your API key. Without an API key, VAS sales run in test mode.
                  </p>
                </div>
              </div>

              {/* Daily Sales Summary Section */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Daily Sales Summary Email
                </h3>
                
                {/* Schedule Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Automatic Daily Email</p>
                      <p className="text-sm text-blue-700 mt-1">
                        A summary email is automatically sent every day at <strong>19:00 SAST</strong> to:
                      </p>
                      <ul className="text-sm text-blue-700 mt-2 space-y-1">
                        <li>• bosveldbh@gmail.com</li>
                      </ul>
                      <p className="text-xs text-blue-600 mt-2">
                        Includes: Cash Sales, Card Sales, Account Sales, Refunds, Petty Cash, Total Sales
                      </p>
                    </div>
                  </div>
                </div>

                {/* Manual Test Section */}
                <div className="p-4 bg-gray-50 border rounded-lg">
                  <h4 className="font-medium mb-3">Test / Manual Send</h4>
                  <div className="flex items-center gap-3 mb-4">
                    <Label className="whitespace-nowrap">Select Date:</Label>
                    <Input
                      type="date"
                      value={dailySummaryDate}
                      onChange={(e) => setDailySummaryDate(e.target.value)}
                      className="w-48"
                    />
                    <Button 
                      variant="outline" 
                      onClick={handlePreviewDailySummary}
                      disabled={dailySummaryLoading}
                    >
                      {dailySummaryLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      Preview
                    </Button>
                    <Button 
                      onClick={handleSendDailySummary}
                      disabled={dailySummarySending}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {dailySummarySending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Now
                    </Button>
                  </div>

                  {/* Preview Display */}
                  {dailySummaryPreview && dailySummaryPreview.summary && (
                    <div className="mt-4 p-4 bg-white border rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-700">
                          Summary for {dailySummaryPreview.summary.date}
                        </h5>
                        {dailySummaryPreview.status === "success" && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            ✓ Email Sent
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between p-2 bg-green-50 rounded">
                          <span>💵 Cash Sales:</span>
                          <span className="font-bold text-green-700">R {dailySummaryPreview.summary.cash_sales?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-blue-50 rounded">
                          <span>💳 Card Sales:</span>
                          <span className="font-bold text-blue-700">R {dailySummaryPreview.summary.card_sales?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-purple-50 rounded">
                          <span>📋 Account Sales:</span>
                          <span className="font-bold text-purple-700">R {dailySummaryPreview.summary.account_sales?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-red-50 rounded">
                          <span>↩️ Refunds:</span>
                          <span className="font-bold text-red-700">R {dailySummaryPreview.summary.refunds?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-amber-50 rounded">
                          <span>🧾 Petty Cash:</span>
                          <span className="font-bold text-amber-700">R {dailySummaryPreview.summary.petty_cash?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="flex justify-between p-2 bg-gray-100 rounded">
                          <span>Transactions:</span>
                          <span className="font-bold">{dailySummaryPreview.summary.transaction_count}</span>
                        </div>
                      </div>
                      <div className="mt-3 p-3 bg-green-100 rounded-lg flex justify-between items-center">
                        <span className="font-semibold text-green-800">TOTAL SALES:</span>
                        <span className="text-xl font-bold text-green-800">
                          R {dailySummaryPreview.summary.total_sales?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories & Departments Tab */}
        <TabsContent value="categories">
          <div className="grid grid-cols-2 gap-4">
            {/* Categories Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderTree className="h-5 w-5" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500">
                  Categories group your products (e.g., Groceries, Hardware, Animal Feed)
                </p>
                
                {/* Add Category */}
                <div className="flex gap-2">
                  <Input
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New category name"
                    onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                  />
                  <Button onClick={handleAddCategory} disabled={loadingCatDept}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Categories List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                    <div key={cat.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-purple-700">{cat.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {getDepartmentsByCategory(cat.id).length} departments
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCategoryOrDepartment(cat.id, cat.name, 'category')}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          title="Delete category"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {/* Show departments under this category */}
                      {getDepartmentsByCategory(cat.id).length > 0 && (
                        <div className="mt-2 pl-3 border-l-2 border-purple-200 space-y-1">
                          {getDepartmentsByCategory(cat.id).map(dept => (
                            <div key={dept.id} className="flex justify-between items-center text-sm text-gray-600">
                              <span>• {dept.name}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCategoryOrDepartment(dept.id, dept.name, 'department')}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                title="Delete department"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">No categories yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Departments Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderTree className="h-5 w-5" />
                  Departments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500">
                  Departments are sub-groups within a category (e.g., Paint, Plumbing within Hardware)
                </p>
                
                {/* Add Department */}
                <div className="space-y-2">
                  <Select value={selectedCategoryForDept} onValueChange={setSelectedCategoryForDept}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category first..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input
                      value={newDepartmentName}
                      onChange={(e) => setNewDepartmentName(e.target.value)}
                      placeholder="New department name"
                      disabled={!selectedCategoryForDept}
                      onKeyDown={(e) => e.key === "Enter" && handleAddDepartment()}
                    />
                    <Button onClick={handleAddDepartment} disabled={loadingCatDept || !selectedCategoryForDept}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Unassigned Departments */}
                {unassignedDepartments.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-orange-600 mb-2">
                      Unassigned Departments ({unassignedDepartments.length})
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Assign these to a category or delete them:
                    </p>
                    <div className="space-y-2">
                      {unassignedDepartments.map(dept => (
                        <div key={dept.id} className="flex items-center gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                          <span className="flex-1 text-sm">{dept.name}</span>
                          <Select onValueChange={(catId) => handleAssignDepartmentToCategory(dept.id, catId)}>
                            <SelectTrigger className="w-40 h-8">
                              <SelectValue placeholder="Assign to..." />
                            </SelectTrigger>
                            <SelectContent>
                              {[...categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategoryOrDepartment(dept.id, dept.name, 'department')}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            title="Delete department"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data Utilities Tab */}

        <TabsContent value="loyalty">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-purple-600" />
                Loyalty Cashback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Default Cashback (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loyaltySettings.cashback_percent}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, cashback_percent: parseFloat(e.target.value || 0) }))}
                    data-testid="loyalty-cashback-percent-input"
                  />
                  <p className="text-xs text-gray-500">Used for customers with "Default" type.</p>
                </div>
                <div className="space-y-2">
                  <Label>Low Spend Cashback (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loyaltySettings.low_spend_percent}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, low_spend_percent: parseFloat(e.target.value || 0) }))}
                    data-testid="loyalty-low-spend-percent-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>High Spend Cashback (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loyaltySettings.high_spend_percent}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, high_spend_percent: parseFloat(e.target.value || 0) }))}
                    data-testid="loyalty-high-spend-percent-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Exclude Items With Markup ≤ (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loyaltySettings.exclude_markup_threshold}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, exclude_markup_threshold: parseFloat(e.target.value || 0) }))}
                    data-testid="loyalty-exclude-markup-threshold-input"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!loyaltySettings.enabled}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, enabled: e.target.checked }))}
                    data-testid="loyalty-enabled-toggle"
                  />
                  <span>Enable Loyalty Cashback</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!loyaltySettings.auto_post_enabled}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, auto_post_enabled: e.target.checked }))}
                    data-testid="loyalty-auto-post-toggle"
                  />
                  <span>Auto Post Monthly on day</span>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    className="w-20 h-8"
                    value={loyaltySettings.auto_post_day ?? 26}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setLoyaltySettings(prev => ({
                        ...prev,
                        auto_post_day: Number.isFinite(v) ? Math.max(1, Math.min(28, v)) : 26,
                      }));
                    }}
                    data-testid="loyalty-auto-post-day"
                  />
                  <span className="text-xs text-gray-500">of each month at 23:55 (SAST)</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!loyaltySettings.no_cap}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, no_cap: e.target.checked }))}
                    data-testid="loyalty-no-cap-toggle"
                  />
                  <span>No Cap</span>
                </label>
              </div>

              {!loyaltySettings.no_cap && (
                <div className="max-w-sm space-y-2">
                  <Label>Monthly Cap Amount (R)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loyaltySettings.monthly_cap_amount || ""}
                    onChange={(e) => setLoyaltySettings(prev => ({ ...prev, monthly_cap_amount: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                    data-testid="loyalty-monthly-cap-input"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleSaveLoyaltySettings} disabled={savingLoyalty} data-testid="loyalty-save-settings-button">
                  {savingLoyalty ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save Loyalty Settings
                </Button>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="space-y-2">
                    <Label>Preview Year</Label>
                    <Input
                      type="number"
                      value={loyaltyYear}
                      onChange={(e) => setLoyaltyYear(parseInt(e.target.value || new Date().getFullYear(), 10))}
                      data-testid="loyalty-preview-year-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preview Month</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={loyaltyMonth}
                      onChange={(e) => setLoyaltyMonth(parseInt(e.target.value || 1, 10))}
                      data-testid="loyalty-preview-month-input"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={handlePreviewLoyalty} disabled={previewingLoyalty} data-testid="loyalty-preview-button">
                      {previewingLoyalty ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Preview
                    </Button>
                    <Button onClick={handleRunLoyalty} disabled={runningLoyalty} data-testid="loyalty-run-button">
                      {runningLoyalty ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Run Month-End Cashback
                    </Button>
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={handleScanLoyaltyDuplicates}
                      disabled={loyaltyDupeBusy}
                      data-testid="loyalty-scan-duplicates-button"
                      title="Find and reverse duplicate loyalty cashback credits for the selected month"
                    >
                      {loyaltyDupeBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Scan &amp; Reverse Duplicates
                    </Button>
                    <Button
                      variant="outline"
                      className="border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                      onClick={handleRestoreOverReversed}
                      disabled={loyaltyDupeBusy}
                      data-testid="loyalty-restore-button"
                      title="Restore customers whose ENTIRE month cashback was wiped (both the duplicate AND the legitimate credit were reversed)"
                    >
                      {loyaltyDupeBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Restore Over-Reversed
                    </Button>
                    <Button
                      variant="outline"
                      className="border-purple-400 text-purple-800 hover:bg-purple-50"
                      onClick={handleFixFutureDatedCredits}
                      disabled={runningLoyalty}
                      data-testid="loyalty-fix-future-dates-button"
                      title="If cashback was run mid-month, credits get dated to the last day of the month and are hidden from statements. Re-date them to today."
                    >
                      {runningLoyalty ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Fix Future-Dated Credits
                    </Button>
                  </div>
                </div>

                {loyaltyPreview && (
                  <div className="space-y-3" data-testid="loyalty-preview-results">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                      <div className="p-3 bg-white rounded border"><div className="text-gray-500">Eligible Customers</div><div className="font-semibold">{loyaltyPreview.summary?.customers_eligible || 0}</div></div>
                      <div className="p-3 bg-white rounded border"><div className="text-gray-500">Eligible Spend</div><div className="font-semibold">R {(loyaltyPreview.summary?.total_eligible_spend || 0).toFixed(2)}</div></div>
                      <div className="p-3 bg-white rounded border"><div className="text-gray-500">Projected Cashback</div><div className="font-semibold">R {(loyaltyPreview.summary?.projected_cashback_total || 0).toFixed(2)}</div></div>
                      <div className="p-3 bg-white rounded border"><div className="text-gray-500">Cashback as % of GP</div><div className="font-semibold">{(loyaltyPreview.summary?.cashback_as_percent_of_gp || 0).toFixed(2)}%</div></div>
                    </div>

                    {/* Diagnostic: line-item exclusion counters — shows WHY customers may not be credited */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="loyalty-exclusion-counters">
                      <div className="p-2 bg-white rounded border">
                        <div className="text-gray-500">Line items scanned</div>
                        <div className="font-semibold text-gray-800">{loyaltyPreview.summary?.line_items_scanned || 0}</div>
                      </div>
                      <div className="p-2 bg-white rounded border border-amber-200">
                        <div className="text-amber-700">Skipped — no customer_id</div>
                        <div className="font-semibold text-amber-800">{loyaltyPreview.summary?.excluded_no_customer || 0}</div>
                      </div>
                      <div className="p-2 bg-white rounded border border-amber-200">
                        <div className="text-amber-700">Skipped — markup ≤ {loyaltyPreview.exclude_markup_threshold ?? 5}%</div>
                        <div className="font-semibold text-amber-800">{loyaltyPreview.summary?.excluded_low_markup || 0}</div>
                      </div>
                      <div className="p-2 bg-white rounded border border-rose-200">
                        <div className="text-rose-700">Skipped — markup missing</div>
                        <div className="font-semibold text-rose-800">{loyaltyPreview.summary?.excluded_missing_markup || 0}</div>
                      </div>
                    </div>

                    {/* Force re-post button — appears when preview shows eligible customers but the run for this month was already recorded */}
                    {(loyaltyPreview.summary?.customers_eligible || 0) > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                        <span className="text-blue-800">
                          Preview shows {loyaltyPreview.summary?.customers_eligible} eligible customer(s).
                          If a previous run posted 0 customers by mistake, use Force re-post to credit them now
                          (per-customer dedup still prevents doubles).
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-400 text-blue-700 hover:bg-blue-100"
                          data-testid="loyalty-force-repost-button"
                          onClick={handleForceRepostLoyalty}
                          disabled={runningLoyalty}
                        >
                          {runningLoyalty ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Force re-post {loyaltyYear}-{String(loyaltyMonth).padStart(2, '0')}
                        </Button>
                      </div>
                    )}


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="loyalty-type-breakdown-cards">
                      <div className="p-3 bg-white rounded border">
                        <div className="text-gray-500">Default Type</div>
                        <div className="text-sm">Customers: {loyaltyPreview.summary?.type_breakdown?.default?.customers || 0}</div>
                        <div className="text-sm">Cashback: R {(loyaltyPreview.summary?.type_breakdown?.default?.cashback_total || 0).toFixed(2)}</div>
                      </div>
                      <div className="p-3 bg-white rounded border border-green-200">
                        <div className="text-green-700">Low Spend Type</div>
                        <div className="text-sm">Customers: {loyaltyPreview.summary?.type_breakdown?.low_spend?.customers || 0}</div>
                        <div className="text-sm">Cashback: R {(loyaltyPreview.summary?.type_breakdown?.low_spend?.cashback_total || 0).toFixed(2)}</div>
                      </div>
                      <div className="p-3 bg-white rounded border border-blue-200">
                        <div className="text-blue-700">High Spend Type</div>
                        <div className="text-sm">Customers: {loyaltyPreview.summary?.type_breakdown?.high_spend?.customers || 0}</div>
                        <div className="text-sm">Cashback: R {(loyaltyPreview.summary?.type_breakdown?.high_spend?.cashback_total || 0).toFixed(2)}</div>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-auto border rounded bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Customer</th>
                            <th className="text-left p-2">Type</th>
                            <th className="text-right p-2">Eligible Spend</th>
                            <th className="text-right p-2">Rate</th>
                            <th className="text-right p-2">Cashback</th>
                            <th className="text-center p-2 w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(loyaltyPreview.rows || []).slice(0, 100).map((row) => (
                            <tr key={row.customer_id}>
                              <td className="p-2 border-t">{row.customer_name}</td>
                              <td className="p-2 border-t">{row.loyalty_customer_type || "default"}</td>
                              <td className="p-2 border-t text-right">R {(row.eligible_spend || 0).toFixed(2)}</td>
                              <td className="p-2 border-t text-right">{(row.applied_cashback_percent || row.cashback_percent || 0).toFixed(2)}%</td>
                              <td className="p-2 border-t text-right font-semibold">R {(row.cashback_amount || 0).toFixed(2)}</td>
                              <td className="p-2 border-t text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`loyalty-remove-${row.customer_id}`}
                                  onClick={() => handleExcludeFromLoyalty(row.customer_id, row.customer_name)}
                                  title="Remove from loyalty list"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Show excluded customers if any */}
                    {loyaltyPreview.excluded_customers?.length > 0 && (
                      <div className="p-3 bg-gray-50 rounded border text-sm">
                        <div className="font-medium text-gray-600 mb-1">Excluded customers ({loyaltyPreview.excluded_customers.length}):</div>
                        <div className="flex flex-wrap gap-2">
                          {loyaltyPreview.excluded_customers.map((exc) => (
                            <span key={exc.customer_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-xs">
                              {exc.customer_name}
                              <button
                                className="text-blue-500 hover:text-blue-700 ml-1 font-medium"
                                onClick={() => handleRestoreToLoyalty(exc.customer_id)}
                                title="Restore to loyalty list"
                              >
                                Restore
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="utilities">
          <div className="space-y-6">
            <Card className="border-blue-200" data-testid="system-valuation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">📊</span>
                  System Valuation Report
                </CardTitle>
                <CardDescription>
                  Generates a fresh PDF valuation of the POS platform (replacement cost,
                  market-comparable and 3-year value-in-use methodologies) and emails it to you
                  as an attachment. Re-run whenever you want a current snapshot — e.g. after a
                  major feature ships, at insurance renewal or for a buyer conversation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1">
                    <Label htmlFor="valuation-email">Send to email</Label>
                    <Input
                      id="valuation-email"
                      type="email"
                      value={valuationEmail}
                      onChange={(e) => setValuationEmail(e.target.value)}
                      placeholder="herman@bosveldbh.co.za"
                      data-testid="valuation-email-input"
                    />
                  </div>
                  <Button
                    onClick={handleEmailSystemValuation}
                    disabled={valuationSending}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="email-system-valuation-btn"
                  >
                    {valuationSending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Generate &amp; Email PDF</>
                    )}
                  </Button>
                  <Button
                    onClick={handleDownloadSystemValuation}
                    variant="outline"
                    data-testid="download-system-valuation-btn"
                  >
                    <Receipt className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Current headline range: <b>R 2.20M – R 2.80M</b> fair market value.
                  The PDF is regenerated fresh every time — no stale data.
                </p>
              </CardContent>
            </Card>

            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">🧮</span>
                  Profit Audit (Ground Truth)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Walks every sale for the current month line-by-line and totals revenue, COGS and profit per product.
                  Refunds, voided transactions, services, loans and withdrawals are excluded. Revenue is converted to excl-VAT
                  using each product's actual VAT category (Zero-rated / Exempt items not divided). Use this to compare against
                  the Income Statement and Financial Summary so you know which one is right.
                </p>
                <Button
                  onClick={handleProfitAudit}
                  disabled={profitAuditRunning}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="profit-audit-btn"
                >
                  {profitAuditRunning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Computing…</>
                  ) : (
                    <>Run Profit Audit (current month)</>
                  )}
                </Button>

                {profitAuditResult && (
                  <div className="mt-2 p-4 rounded-lg border bg-green-50 border-green-200" data-testid="profit-audit-result">
                    <p className="text-sm font-medium text-green-800 mb-3">
                      Period: {profitAuditResult.period.month}/{profitAuditResult.period.year} ·
                      {" "}{profitAuditResult.sales_counted} sales counted
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 text-sm">
                      <div className="bg-white rounded p-2 border">
                        <div className="text-xs text-gray-500">Revenue (incl VAT)</div>
                        <div className="font-bold text-base">R {(profitAuditResult.totals.revenue_incl_vat || 0).toLocaleString("en-ZA", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-xs text-gray-500">Revenue (excl VAT)</div>
                        <div className="font-bold text-base">R {(profitAuditResult.totals.revenue_excl_vat || 0).toLocaleString("en-ZA", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-xs text-gray-500">COGS</div>
                        <div className="font-bold text-base">R {(profitAuditResult.totals.cogs || 0).toLocaleString("en-ZA", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </div>
                      <div className="bg-white rounded p-2 border border-green-300">
                        <div className="text-xs text-gray-500">Gross Profit</div>
                        <div className="font-bold text-base text-green-700">R {(profitAuditResult.totals.gross_profit || 0).toLocaleString("en-ZA", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-xs text-gray-500">Gross Margin %</div>
                        <div className="font-bold text-base">{profitAuditResult.totals.gross_margin_pct}%</div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 mb-3">
                      Skipped — services/loans/withdrawals: {profitAuditResult.skipped.service_loan_withdrawal_lines} ·
                      {" "}no matching product: {profitAuditResult.skipped.no_matching_product} ·
                      {" "}zero-cost lines: {profitAuditResult.skipped.lines_with_zero_cost}
                    </div>

                    <p className="text-xs font-medium mb-1">Top 50 products by profit:</p>
                    <div className="max-h-96 overflow-y-auto bg-white rounded border">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="border-b">
                            <th className="px-2 py-1 text-left">PLU</th>
                            <th className="px-2 py-1 text-left">Name</th>
                            <th className="px-2 py-1 text-right">Units</th>
                            <th className="px-2 py-1 text-right">Revenue</th>
                            <th className="px-2 py-1 text-right">COGS</th>
                            <th className="px-2 py-1 text-right">Profit</th>
                            <th className="px-2 py-1 text-right">GP%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profitAuditResult.top_50_by_profit.map((r, i) => (
                            <tr key={i} className="border-b hover:bg-green-50">
                              <td className="px-2 py-1 font-mono">{r.plu_code || "—"}</td>
                              <td className="px-2 py-1">{(r.name || "").substring(0, 40)}</td>
                              <td className="px-2 py-1 text-right">{r.units_sold}</td>
                              <td className="px-2 py-1 text-right">R {(r.revenue_excl_vat || 0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                              <td className="px-2 py-1 text-right">R {(r.cogs || 0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                              <td className="px-2 py-1 text-right font-semibold text-green-700">R {(r.profit || 0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                              <td className="px-2 py-1 text-right">{r.margin_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {profitAuditResult.bottom_10_by_profit && profitAuditResult.bottom_10_by_profit.length > 0 && (
                      <>
                        <p className="text-xs font-medium mt-3 mb-1 text-red-700">Bottom 10 (loss-making lines):</p>
                        <div className="bg-white rounded border max-h-48 overflow-y-auto">
                          <table className="w-full text-xs">
                            <tbody>
                              {profitAuditResult.bottom_10_by_profit.map((r, i) => (
                                <tr key={i} className="border-b">
                                  <td className="px-2 py-1 font-mono">{r.plu_code}</td>
                                  <td className="px-2 py-1">{(r.name || "").substring(0, 40)}</td>
                                  <td className="px-2 py-1 text-right">{r.units_sold}</td>
                                  <td className="px-2 py-1 text-right text-red-700 font-semibold">R {(r.profit || 0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-amber-600" />
                  Fix Historical Profit (Cost-Price Backfill)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Walks every saved sale and back-fills the cost price on any line item whose <code>cost_price</code> is missing or 0,
                  using the matching product's current cost. Loans, withdrawals, services and once-off items are skipped. Existing real
                  costs are never overwritten. Use this once after the deploy that introduced the cost-price stamping fix to repair
                  inflated "Gross Profit" totals on historical sales (e.g. anything that came through Convert-Quote-to-Sale).
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleProfitBackfillDryRun}
                    disabled={profitBackfillRunning}
                    data-testid="profit-backfill-dry-run-btn"
                  >
                    {profitBackfillRunning ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking…</>
                    ) : (
                      <>Preview (dry-run)</>
                    )}
                  </Button>
                  <Button
                    onClick={handleProfitBackfillRun}
                    disabled={profitBackfillRunning}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="profit-backfill-run-btn"
                  >
                    {profitBackfillRunning ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fixing…</>
                    ) : (
                      <>Run Fix Now</>
                    )}
                  </Button>
                </div>

                {profitBackfillResult && (
                  <div
                    className={`mt-2 p-4 rounded-lg border ${profitBackfillResult.dry_run ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}
                    data-testid="profit-backfill-result"
                  >
                    <p className={`text-sm font-medium ${profitBackfillResult.dry_run ? "text-amber-800" : "text-green-800"}`}>
                      {profitBackfillResult.dry_run ? "Dry-run preview:" : "✓ Fix complete:"}
                    </p>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>Sales scanned: {profitBackfillResult.sales_scanned}</li>
                      <li>Sales {profitBackfillResult.dry_run ? "that would be updated" : "updated"}: <strong>{profitBackfillResult.sales_updated}</strong></li>
                      <li>Line items {profitBackfillResult.dry_run ? "that would be fixed" : "fixed"}: <strong>{profitBackfillResult.items_fixed}</strong></li>
                      <li className="text-gray-500">Skipped (refunds): {profitBackfillResult.skipped_refunds || 0}</li>
                      <li className="text-gray-500">Skipped (services/loans/withdrawals): {profitBackfillResult.skipped_service_lines}</li>
                      <li className="text-gray-500">Skipped (no matching product): {profitBackfillResult.skipped_no_product_match}</li>
                    </ul>
                    {(profitBackfillResult.examples || []).length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium">Highest-value examples (sorted by line total):</p>
                        <div className="mt-1 max-h-72 overflow-y-auto text-xs space-y-1">
                          {profitBackfillResult.examples.map((e, i) => (
                            <div key={i} className="font-mono break-all">
                              <span className="text-amber-800 font-semibold">R{(e.line_total || 0).toFixed(2)}</span>
                              {" · "}{e.transaction_number}{" · "}
                              {e.name}{" · qty "}{e.qty}{" · cost R"}{(e.old_cost || 0).toFixed(2)}{" → R"}{(e.new_cost || 0).toFixed(2)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-purple-600" />
                  Customer Reconciliation Sync
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  This utility syncs all existing reconciliation records with their corresponding sales records. 
                  Use this if customer invoice balances are not updating correctly after allocating payments.
                </p>
                
                <div className="flex items-center gap-4">
                  <Button 
                    onClick={handleSyncReconciliations} 
                    disabled={syncingRecons}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {syncingRecons ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                    ) : (
                      <><RotateCcw className="h-4 w-4 mr-2" /> Sync Reconciliation Data</>
                    )}
                  </Button>
                </div>

                {syncResult && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-medium text-green-800">
                      ✓ Sync Complete
                    </p>
                    <ul className="text-sm text-green-700 mt-2 space-y-1">
                      <li>Reconciliations processed: {syncResult.reconciliations_processed}</li>
                      <li>Invoices updated: {syncResult.invoices_updated}</li>
                    </ul>
                    {syncResult.details && syncResult.details.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-green-800">Updated invoices:</p>
                        <div className="mt-1 max-h-40 overflow-y-auto">
                          {syncResult.details.map((item, index) => (
                            <div key={index} className="text-xs text-green-600">
                              {item.invoice}: R{item.total?.toFixed(2)} → Paid: R{item.amount_paid?.toFixed(2)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-teal-600" />
                  Product Location Sync
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Reconciles each product's <strong>location</strong>, <strong>shelf_number</strong> and <strong>bin_number</strong> fields so
                  they all match. Run this once if the <strong>Print Stocktake → Per Shelf</strong> sheet is still showing
                  products under an old shelf they were moved away from. Safe to re-run anytime.
                </p>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleSyncLocations}
                    disabled={syncingLocations}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="sync-locations-btn"
                  >
                    {syncingLocations ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Syncing...</>
                    ) : (
                      <><RotateCcw className="h-4 w-4 mr-2" /> Sync Product Locations</>
                    )}
                  </Button>
                </div>

                {locationSyncResult && (
                  <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg" data-testid="sync-locations-result">
                    <p className="text-sm font-medium text-teal-800">
                      ✓ Scanned {locationSyncResult.scanned} products, healed {locationSyncResult.healed} with drifted location fields.
                    </p>
                    {locationSyncResult.examples?.length > 0 && (
                      <div className="mt-3 max-h-40 overflow-y-auto">
                        <p className="text-xs font-medium text-teal-800 mb-1">Sample changes:</p>
                        {locationSyncResult.examples.map((ex, i) => (
                          <div key={i} className="text-xs text-teal-700 font-mono">
                            {ex.before.location || '∅'} / sn={ex.before.shelf_number || '∅'} bn={ex.before.bin_number || '∅'}
                            {' → '}
                            {ex.after.location || '∅'} / sn={ex.after.shelf_number || '∅'} bn={ex.after.bin_number || '∅'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-amber-600" />
                  Re-evaluate Invoice Payments (Dry-Run)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Scans every account-sale invoice and debit journal, comparing the stored{" "}
                  <code className="text-xs bg-gray-100 px-1 rounded">amount_paid</code> against the
                  total of actual <strong>reconciliation</strong> records. Invoices wrongly marked
                  as fully paid (where no allocation exists) are flagged so the unmatched payment
                  can be allocated correctly.
                </p>
                <p className="text-sm text-amber-700 font-medium">
                  Dry-run is the default — nothing changes until you click "Apply".
                </p>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col">
                    <Label htmlFor="reeval-customer-id" className="text-xs text-gray-600 mb-1">
                      Restrict to one customer (optional)
                    </Label>
                    <Input
                      id="reeval-customer-id"
                      data-testid="reeval-customer-id-input"
                      value={reevalCustomerFilter}
                      onChange={(e) => setReevalCustomerFilter(e.target.value)}
                      placeholder="customer_id (leave blank for all)"
                      className="w-72"
                    />
                  </div>
                  <Button
                    onClick={() => handleReevaluateInvoices(false)}
                    disabled={reevaluating}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="reeval-dry-run-btn"
                  >
                    {reevaluating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning...</>
                    ) : (
                      <><RotateCcw className="h-4 w-4 mr-2" /> Run Dry-Run Scan</>
                    )}
                  </Button>
                  {reevaluateResult && (reevaluateResult.summary?.mismatches_found || 0) > 0 && reevaluateResult.dry_run && (
                    <Button
                      onClick={() => handleReevaluateInvoices(true)}
                      disabled={applyingReeval}
                      variant="destructive"
                      data-testid="reeval-apply-btn"
                    >
                      {applyingReeval ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>
                      ) : (
                        <>Apply Corrections ({reevaluateResult.summary.mismatches_found})</>
                      )}
                    </Button>
                  )}
                </div>

                {reevaluateResult && (
                  <div
                    className={`mt-4 p-4 rounded-lg border ${
                      reevaluateResult.summary.mismatches_found > 0
                        ? "bg-amber-50 border-amber-300"
                        : "bg-green-50 border-green-200"
                    }`}
                    data-testid="reeval-result"
                  >
                    <p className="text-sm font-medium">
                      {reevaluateResult.applied
                        ? `✓ Applied ${reevaluateResult.summary.invoices_corrected} correction(s)`
                        : reevaluateResult.summary.mismatches_found > 0
                        ? `⚠ ${reevaluateResult.summary.mismatches_found} mismatch(es) found (dry-run)`
                        : "✓ No mismatches found"}
                    </p>
                    <ul className="text-xs text-gray-700 mt-2 space-y-1">
                      <li>Customers in scope: {reevaluateResult.summary.customers_in_scope}</li>
                      <li>Invoices scanned: {reevaluateResult.summary.invoices_scanned}</li>
                      <li>Debit journals scanned: {reevaluateResult.summary.journals_scanned}</li>
                      <li>
                        Total balance to restore: R{(reevaluateResult.summary.total_balance_to_restore || 0).toFixed(2)}
                      </li>
                    </ul>

                    {reevaluateResult.mismatches?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium mb-1">
                          Top {Math.min(50, reevaluateResult.mismatches.length)} mismatches:
                        </p>
                        <div className="max-h-72 overflow-y-auto border rounded bg-white">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="text-left p-1.5">Customer</th>
                                <th className="text-left p-1.5">Invoice</th>
                                <th className="text-left p-1.5">Date</th>
                                <th className="text-right p-1.5">Total</th>
                                <th className="text-right p-1.5">Stored Paid</th>
                                <th className="text-right p-1.5">Actual Paid</th>
                                <th className="text-right p-1.5">Restore</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reevaluateResult.mismatches.slice(0, 50).map((m, i) => (
                                <tr key={i} className="border-t" data-testid={`reeval-row-${i}`}>
                                  <td className="p-1.5">{m.customer_name}</td>
                                  <td className="p-1.5">{m.invoice_number}</td>
                                  <td className="p-1.5">{m.invoice_date}</td>
                                  <td className="p-1.5 text-right">R{m.invoice_total.toFixed(2)}</td>
                                  <td className="p-1.5 text-right text-red-600">R{m.current_amount_paid.toFixed(2)}</td>
                                  <td className="p-1.5 text-right text-green-700">R{m.actual_reconciled_amount.toFixed(2)}</td>
                                  <td className="p-1.5 text-right font-semibold">R{m.delta.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Printer className="h-5 w-5 text-blue-600" />
                  Sticker Print Calibration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Nudge shelf-talker / sticker print position when content cuts off on the edge of
                  the label. The printable area is <strong>38 × 29 mm</strong> inside the 40 × 30 mm physical
                  sticker (1 mm dead margin horizontally, 0.5 mm vertically).
                  Negative <strong>X</strong> shifts left, negative <strong>Y</strong> shifts up.
                  Use the <strong>Test Print</strong> button to fine-tune, then <strong>Save</strong>.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm">Horizontal offset (X)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setStickerOffsetX((v) => parseFloat((v - 0.5).toFixed(2)))}
                          data-testid="sticker-x-decrease-btn"
                        >
                          ← 0.5mm
                        </Button>
                        <Input
                          type="number"
                          step="0.1"
                          value={stickerOffsetX}
                          onChange={(e) => setStickerOffsetX(parseFloat(e.target.value) || 0)}
                          className="w-24 text-center"
                          data-testid="sticker-x-input"
                        />
                        <span className="text-sm text-gray-500">mm</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setStickerOffsetX((v) => parseFloat((v + 0.5).toFixed(2)))}
                          data-testid="sticker-x-increase-btn"
                        >
                          0.5mm →
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm">Vertical offset (Y)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setStickerOffsetY((v) => parseFloat((v - 0.5).toFixed(2)))}
                          data-testid="sticker-y-decrease-btn"
                        >
                          ↑ 0.5mm
                        </Button>
                        <Input
                          type="number"
                          step="0.1"
                          value={stickerOffsetY}
                          onChange={(e) => setStickerOffsetY(parseFloat(e.target.value) || 0)}
                          className="w-24 text-center"
                          data-testid="sticker-y-input"
                        />
                        <span className="text-sm text-gray-500">mm</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setStickerOffsetY((v) => parseFloat((v + 0.5).toFixed(2)))}
                          data-testid="sticker-y-increase-btn"
                        >
                          ↓ 0.5mm
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        onClick={handleTestPrintSticker}
                        variant="outline"
                        className="border-blue-600 text-blue-700 hover:bg-blue-50"
                        data-testid="sticker-test-print-btn"
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Test Print
                      </Button>
                      <Button
                        onClick={handleSaveCalibration}
                        disabled={savingCalibration}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="sticker-save-calibration-btn"
                      >
                        {savingCalibration ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Calibration
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setStickerOffsetX(0); setStickerOffsetY(0); }}
                        data-testid="sticker-reset-btn"
                      >
                        Reset to defaults (0, 0)
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-col items-center">
                    <Label className="text-xs text-gray-500 mb-2">Live preview (40 × 30 mm, scaled 6×) — dashed = printable area</Label>
                    <div
                      style={{
                        width: '240px',
                        height: '180px',
                        background: '#fff',
                        border: '1px solid #94a3b8',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      data-testid="sticker-preview"
                    >
                      {/* Printable safe-area indicator: 38mm × 29mm inset by 1mm horiz / 0.5mm vert */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '3px',    // 0.5mm × 6
                          left: '6px',   // 1mm × 6
                          right: '6px',
                          bottom: '3px',
                          border: '1px dashed #cbd5e1',
                          pointerEvents: 'none',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          paddingTop: `${1.5 * 6}px`,
                          paddingRight: `${2.5 * 6}px`,
                          paddingBottom: `${1.5 * 6}px`,
                          paddingLeft: `${2.5 * 6}px`,
                          transform: `translate(${(stickerOffsetX || 0) * 6}px, ${(stickerOffsetY || 0) * 6}px)`,
                          transformOrigin: 'top left',
                          boxSizing: 'border-box',
                          fontFamily: 'Arial, sans-serif',
                          color: '#000',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ background: '#000', color: '#fff', padding: '1.5px 5px', fontWeight: 900, fontSize: '11px', letterSpacing: '0.8px' }}>
                            {(settings.short_name || 'BBH').slice(0, 6)}
                          </span>
                          <span style={{ fontSize: '18px', fontWeight: 900 }}>R16.90</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', fontWeight: 'bold', marginTop: '1px' }}>
                          <span>PLU: 3554</span>
                          <span>Shelf:P1-9 Bin:0002</span>
                        </div>
                        {sampleBarcodeImg && (
                          <img
                            src={sampleBarcodeImg}
                            alt="sample barcode"
                            style={{
                              width: '216px',  // 36mm × 6
                              maxHeight: '66px',  // 11mm × 6
                              objectFit: 'contain',
                              margin: '2px auto 0 auto',
                              display: 'block',
                            }}
                          />
                        )}
                        <div style={{ marginTop: 'auto', textAlign: 'center', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', lineHeight: 1.0, maxHeight: '36px', overflow: 'hidden' }}>
                          PVC GUTTER HALF ROUND BRACKET
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Current: X = <strong>{stickerOffsetX}</strong> mm, Y = <strong>{stickerOffsetY}</strong> mm
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            </Card>
          </div>
        </TabsContent>
        {/* VAT Company Tab */}
        <TabsContent value="vat-company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                VAT Company Profile
              </CardTitle>
              <p className="text-sm text-gray-500">
                This profile is used when generating a VAT Invoice from an existing invoice on the Document Lookup page.
                Enter the details of your VAT-registered entity here — they will be saved once and stamped onto every VAT invoice.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Details */}
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Company Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input data-testid="vat-company-name" value={vatCompany.company_name} onChange={e => setVatCompany(p => ({...p, company_name: e.target.value}))} placeholder="VAT Registered Entity Name" />
                  </div>
                  <div>
                    <Label>VAT Number *</Label>
                    <Input data-testid="vat-company-vat-number" value={vatCompany.vat_number} onChange={e => setVatCompany(p => ({...p, vat_number: e.target.value}))} placeholder="e.g. 4123456789" />
                  </div>
                  <div>
                    <Label>Registration Number</Label>
                    <Input data-testid="vat-company-reg" value={vatCompany.registration_number} onChange={e => setVatCompany(p => ({...p, registration_number: e.target.value}))} placeholder="e.g. 2005/012345/07" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={vatCompany.phone} onChange={e => setVatCompany(p => ({...p, phone: e.target.value}))} placeholder="Phone number" />
                  </div>
                  <div className="col-span-2">
                    <Label>Email</Label>
                    <Input value={vatCompany.email} onChange={e => setVatCompany(p => ({...p, email: e.target.value}))} placeholder="email@company.co.za" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Address Line 1</Label>
                    <Input value={vatCompany.address_line1} onChange={e => setVatCompany(p => ({...p, address_line1: e.target.value}))} placeholder="Street address" />
                  </div>
                  <div className="col-span-2">
                    <Label>Address Line 2</Label>
                    <Input value={vatCompany.address_line2} onChange={e => setVatCompany(p => ({...p, address_line2: e.target.value}))} placeholder="Suburb / Building" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={vatCompany.city} onChange={e => setVatCompany(p => ({...p, city: e.target.value}))} placeholder="City" />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Input value={vatCompany.province} onChange={e => setVatCompany(p => ({...p, province: e.target.value}))} placeholder="Province" />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input value={vatCompany.postal_code} onChange={e => setVatCompany(p => ({...p, postal_code: e.target.value}))} placeholder="0000" />
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Banking Details (shown on VAT invoices)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input value={vatCompany.bank_name} onChange={e => setVatCompany(p => ({...p, bank_name: e.target.value}))} placeholder="e.g. First National Bank" />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input value={vatCompany.bank_account_number} onChange={e => setVatCompany(p => ({...p, bank_account_number: e.target.value}))} placeholder="Account number" />
                  </div>
                  <div>
                    <Label>Branch Code</Label>
                    <Input value={vatCompany.bank_branch_code} onChange={e => setVatCompany(p => ({...p, bank_branch_code: e.target.value}))} placeholder="e.g. 250655" />
                  </div>
                  <div>
                    <Label>Account Type</Label>
                    <Input value={vatCompany.bank_account_type} onChange={e => setVatCompany(p => ({...p, bank_account_type: e.target.value}))} placeholder="e.g. Cheque / Current" />
                  </div>
                </div>
              </div>

              <Button data-testid="save-vat-company-btn" onClick={saveVatCompany} disabled={savingVatCompany} className="w-full">
                {savingVatCompany ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save VAT Company Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default CompanySettings;
